import { useEffect, useState } from 'react'
import { TOOLTIP } from '../../../../constants/tooltips'
import { AquiferFieldsEdit } from '../../../../types/form-types'
import { SetValue, ValidationErrors } from '../../../hooks/use-edit-form'
import LookupField from '../../wells/lookup-field'
import { FormField, Heading, ReadOnlyField, Typeahead, TypeaheadOption, useTitleChange } from '@msaf/core-react'
import { useWellState } from '../../../context/well-context'

interface AquiferEditFormProps {
  isLoading: boolean
  fields: Partial<AquiferFieldsEdit>
  setValue: SetValue<AquiferFieldsEdit>
  validationErrors: ValidationErrors<AquiferFieldsEdit>
}

export function EditAquiferForm({ isLoading, fields, setValue, validationErrors }: AquiferEditFormProps) {
  useTitleChange('Edit Aquifer | Aquifer')
  const {
    wellState: { councilId },
  } = useWellState()

  const [selectedOption, setSelectedOption] = useState<TypeaheadOption>({
    id: '',
    label: '',
    value: '',
    description: '',
  })
  const [options, setOptions] = useState<TypeaheadOption[]>([])

  useEffect(() => {
    if (Array.isArray(fields.aquifers)) {
      const opts: TypeaheadOption[] = fields.aquifers.map((aquifer) => ({
        id: `${aquifer.id ?? ''}`,
        label: aquifer.aquiferName ?? '',
        value: `${aquifer.id ?? ''}`,
        description: aquifer.description ?? '',
      }))
      setOptions(opts)
    }
  }, [setOptions, fields.aquifers])

  useEffect(() => {
    // Find the passed value in the lookup options and set the selected option
    if (options.length && fields.aquiferId) {
      const selected = options.find((opt: TypeaheadOption) => String(opt.id) === String(fields.aquiferId))
      if (selected) {
        setSelectedOption(selected)
      }
    }
  }, [options, fields.aquiferId])

  const handleChange = (option: TypeaheadOption | null) => {
    setValue('aquiferId', option?.value ?? '')
    setSelectedOption(
      option ?? {
        id: '',
        label: '',
        value: '',
        description: '',
      },
    )
  }
  // Using Typeahead directly because we need the options available within this component
  // for other fields.

  return (
    <>
      <Heading isSkeleton={isLoading} level={2}>
        Aquifer
      </Heading>

      <Heading isSkeleton={isLoading} level={3}>
        Details
      </Heading>

      <FormField
        isSkeleton={isLoading}
        labelId='aquiferName'
        label='Aquifer name'
        toolTipMessage={TOOLTIP.aquiferName}
        validationMessages={validationErrors?.aquiferId}
      >
        <Typeahead
          isSkeleton={isLoading}
          selectType='default'
          options={options}
          labelledBy='aquiferName'
          handleChange={handleChange}
          selectedOption={selectedOption}
          isInvalid={!!validationErrors?.aquiferId}
        />
      </FormField>
      <FormField isSkeleton={isLoading} labelId='aquiferDescription' label='Aquifer description'>
        <ReadOnlyField
          isSkeleton={isLoading}
          labelledBy='aquiferDescription'
          value={selectedOption?.description ?? ''}
        />
      </FormField>
      <FormField
        validationMessages={validationErrors?.aquiferType as Array<string>}
        isSkeleton={isLoading}
        labelId='aquiferType'
        label='Confinement status'
        toolTipMessage={TOOLTIP.aquiferTypeId}
      >
        <LookupField
          isSkeleton={isLoading}
          fieldId='aquiferType'
          labelledBy='aquiferTypeLabel'
          value={`${fields.aquiferType ?? ''}`}
          setValue={({ value }) => setValue('aquiferType', value)}
        />
      </FormField>
      <FormField
        validationMessages={validationErrors?.waterManagementZone as Array<string>}
        isSkeleton={isLoading}
        labelId='waterManagementZone'
        label='Water management zone'
        toolTipMessage={TOOLTIP.waterManagementZoneId}
      >
        <LookupField
          isSkeleton={isLoading}
          fieldId='waterManagementZone'
          labelledBy='waterManagementZoneLabel'
          value={`${fields.waterManagementZone ?? ''}`}
          setValue={({ value }) => setValue('waterManagementZone', value)}
          {...(councilId && { fieldFilters: { councilId: councilId } })}
        />
      </FormField>
      <FormField
        validationMessages={validationErrors?.waterAllocationZone as Array<string>}
        isSkeleton={isLoading}
        labelId='waterAllocationZone'
        label='Water allocation zone'
        toolTipMessage={TOOLTIP.waterAllocationZoneId}
      >
        <LookupField
          isSkeleton={isLoading}
          fieldId='waterAllocationZone'
          labelledBy='waterAllocationZoneLabel'
          value={`${fields.waterAllocationZone ?? ''}`}
          setValue={({ value }) => setValue('waterAllocationZone', value)}
          {...(councilId && { fieldFilters: { councilId: councilId } })}
        />
      </FormField>
    </>
  )
}
